import TableContainer from '@mui/material/TableContainer';
import { styled } from '@mui/material/styles';
import { Table } from '@components/Table';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import StatusChip, { STATUS_PALETTE } from '@components/StatusChip';
import { useTranslation } from '@hooks';
import { TwoStepIntegrationConfig } from '@constants/integrations';
import { getIntegrationIcon } from '../integrationIcons';
import { PlatformCell, EmptyState } from '../styles';

const ActionsWrapper = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    paddingRight: 16,
    // Opaque resting fill so the row hover highlight doesn't bleed through the
    // transparent button variants; :not(:hover) keeps each button's own hover state.
    '& .MuiButtonBase-root:not(:hover)': {
        backgroundColor: 'var(--light-main)',
    },
});

const StatusCell = styled('div')({
    textAlign: 'left',
});

const getColumnNames = t => [
    {
        key: 0,
        component: (
            <Typography variant="subtitle2" align="left">
                {t('SettingsIntegrations.columnPlatform')}
            </Typography>
        ),
    },
    {
        key: 1,
        component: (
            <Typography variant="subtitle2" align="left">
                {t('SettingsIntegrations.columnAccount')}
            </Typography>
        ),
    },
    {
        key: 2,
        component: (
            <Typography variant="subtitle2" align="left">
                {t('SettingsIntegrations.columnStatus')}
            </Typography>
        ),
    },
    { key: 3, component: <Typography variant="subtitle2" align="left" /> },
];

const getRows = (integrations, t, onDisconnect, onReconnect, onManageIntegration) =>
    integrations.map(item => [
        {
            component: (
                <PlatformCell>
                    <span style={{ display: 'flex' }}>{getIntegrationIcon(item.id, 20)}</span>
                    <Typography variant="subtitle2" align="left">
                        {item.name}
                    </Typography>
                </PlatformCell>
            ),
        },
        {
            component: (
                <Typography
                    variant="body2"
                    align="left"
                    sx={{ color: item.accountLabel ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
                >
                    {item.accountLabel || '—'}
                </Typography>
            ),
        },
        {
            component: (
                <StatusCell>
                    {item.pending ? (
                        <StatusChip {...STATUS_PALETTE.warning} label={t('common.inProgress')} />
                    ) : item.connected ? (
                        <StatusChip {...STATUS_PALETTE.success} label={t('SettingsIntegrations.connected')} />
                    ) : (
                        <StatusChip {...STATUS_PALETTE.danger} label={t('common.disconnected')} />
                    )}
                </StatusCell>
            ),
        },
        {
            value: item,
            component: (
                <ActionsWrapper>
                    {item.connected ? (
                        !item.pending &&
                        TwoStepIntegrationConfig[item.id] && (
                            <Button variant="outlined" onClick={() => onManageIntegration(item.id, item.integrationId)}>
                                {t('buttons.manage')}
                            </Button>
                        )
                    ) : (
                        <Button variant="outlined" onClick={() => onReconnect(item.id)}>
                            {t('buttons.reconnect')}
                        </Button>
                    )}
                    <Button variant="danger" onClick={() => onDisconnect(item.id, item.integrationId)}>
                        {t('buttons.delete')}
                    </Button>
                </ActionsWrapper>
            ),
        },
    ]);

const IntegrationsTable = ({ integrations, onAdd, onDisconnect, onReconnect, onManageIntegration }) => {
    const { t } = useTranslation();

    if (!integrations.length) {
        return (
            <EmptyState>
                <Typography variant="title1" sx={{ color: 'var(--text-primary)' }}>
                    {t('SettingsIntegrations.noIntegrationsTitle')}
                </Typography>
                <Typography variant="body2">{t('SettingsIntegrations.noIntegrationsDescription')}</Typography>
                <Button variant="contained" onClick={onAdd}>
                    {t('SettingsIntegrations.addIntegration')}
                </Button>
            </EmptyState>
        );
    }

    return (
        <TableContainer>
            <Table
                columnNames={getColumnNames(t)}
                rows={getRows(integrations, t, onDisconnect, onReconnect, onManageIntegration)}
                isInteractive
                isCustomActionComponent
            />
        </TableContainer>
    );
};

export default IntegrationsTable;
